import { useQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { secondaryNavigationData } from "@/components/navigation/navigationData";
import { GET_CURRENT_USER_QUERY, MY_ORGANISATIONS_QUERY } from "@/graphql/queries";
import { UPDATE_ORGANISATION_MUTATION } from "@/graphql/mutations";

const getNavWithCurrent = (currentName: string) =>
  secondaryNavigationData.map((item) => ({
    ...item,
    current: item.name === currentName,
  }));

export default function EditOrganisation() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_CURRENT_USER_QUERY);
  const org = data?.getCurrentUser?.currentOrganisation;

  const [name, setName] = useState(org?.name || "");
  const [success, setSuccess] = useState(false);
  const [updateOrganisation, { loading: updating, error: updateError }] =
    useMutation(UPDATE_ORGANISATION_MUTATION, {
      onCompleted: () => {
        navigate("/settings/organisations");
      },
      refetchQueries: [{ query: MY_ORGANISATIONS_QUERY }],
      awaitRefetchQueries: true,
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <SecondaryNavigation items={getNavWithCurrent("Organisations")} />
        <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h1 className="text-2xl font-semibold mb-4 text-indigo-700 dark:text-indigo-400">
              Edit Organisation
            </h1>
            <div className="text-gray-500">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
        <SecondaryNavigation items={getNavWithCurrent("Organisations")} />
        <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h1 className="text-2xl font-semibold mb-4 text-indigo-700 dark:text-indigo-400">
              Edit Organisation
            </h1>
            <div className="text-red-500">
              Could not load organisation details.
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    await updateOrganisation({
      variables: {
        id: org.id,
        organisation: { name },
      },
    });
  };

  return (
    <main className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <SecondaryNavigation items={getNavWithCurrent("Organisations")} />
        <section className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-4">
                Edit Organisation
              </h1>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="orgName"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-100"
                  >
                    Organisation Name
                  </label>
                  <input
                    id="orgName"
                    name="orgName"
                    type="text"
                    required
                    className="mt-2 block w-full rounded-md border-0 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    disabled={updating}
                  >
                    {updating ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                    onClick={() => navigate("/settings/organisations")}
                  >
                    Cancel
                  </button>
                </div>
                {updateError && (
                  <div className="text-red-500 text-sm mt-2 dark:text-red-400">
                    {updateError.message}
                  </div>
                )}
                {success && (
                  <div className="text-green-600 text-sm mt-2 dark:text-green-400">
                    Organisation updated successfully.
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
